// Theme-token reference for the Control Center Theme Builder.
// Pure data + pure functions. No React.
//
// This is the editor-side mirror of the player's THEME_TOKEN_MAP
// (hosted-player `src/app/lib/theme-tokens.ts`). The player reads the
// `theme*` keys off the normalized PMM bag and paints each onto the
// matching `--player-*` custom property on its shadow host; we write
// those same keys and use the same mapping to drive the live preview.
//
// Keep this map in lockstep with the player's. If the two disagree, the
// preview lies about what the client will actually see.
//
// Contract notes (load-bearing):
//   - '' means UNSET. The player skips empty tokens so its own shipped
//     CSS default paints. We never store a hex just because an input
//     needed something to display -- see DEFAULT_SWATCHES.
//   - Radius is BASE-ONLY. `themeRadius` drives `--player-radius`; the
//     sm/lg/xl family keeps the player's CSS defaults and is not derived.
//   - accent-hover, border-hover and border-light are derived by the
//     player via color-mix. They have no key and no control.

/** PMM property key -> the `--player-*` custom property it paints. */
export const THEME_TOKEN_MAP = {
  themeAccent: '--player-accent',
  themeTextStrong: '--player-text-strong',
  themeText: '--player-text',
  themeTextMuted: '--player-text-muted',
  themeTextSoft: '--player-text-soft',
  themeTint: '--player-tint',
  themeBorder: '--player-border',
  themeFontFamily: '--player-font-family',
  themeRadius: '--player-radius',

  // Structural brand (4.1.0-beta.3). Beyond the palette: the per-component
  // levers a client needs to reproduce a deliberate look -- WisEye's filled
  // brand-red buttons and enlarged type being the case that drove them.
  //
  // "Filled" is not a separate boolean: a button is filled when
  // `themeButtonBg` carries a value and ghost when it does not, which is the
  // same empty-means-default contract as every other key. The player also
  // collapses the outline into the fill when it is set, so a filled button
  // does not render with a stray grey border.
  themeButtonBg: '--player-action-button-bg',
  themeButtonText: '--player-action-button-color',
  themeButtonSize: '--player-action-button-size',
  themeBodyFontSize: '--player-body-font-size',
  themeTitleFontSize: '--player-underlay-title-size',

  // The rest of the type scale (4.1.0). Title/body/button covered three
  // surfaces and stopped, which left the event date, the tab labels and the
  // list rows with no lever at all -- the three surfaces Oregon State
  // Legislature asked about by name.
  //
  // These replace `themeRem`, a single whole-player scale key that was built
  // and then deleted before any client saw it: it had no safe default. At 16px
  // it was inert and undiscoverable, and at anything larger every existing
  // embed's type grew on upgrade. The accessibility case it was meant to serve
  // is answered by the player instead, which now follows the READER's own
  // browser font-size -- so it needs no preference and reaches the clients who
  // never open the Builder. hosted-player #839.
  //
  // themeListRowFontSize drives documents, agenda AND transcript together, and
  // carries each row's meta line (the timecode) at a fixed proportion. A label
  // that grows while the timecode beside it does not IS the reported bug.
  themeDateFontSize: '--player-underlay-date-size',
  themeTabFontSize: '--player-tab-size',
  themeListRowFontSize: '--player-list-row-size',

  // Themed modal surface (4.1.0-beta.5). The Share/Download/Clip dialogs are
  // a top-layer card that ships white; setting this makes the card follow the
  // client palette, so a dark theme yields a dark card carrying the themed
  // light text instead of light text on a permanent white card. Empty keeps
  // the white-card floor -- where the player paints legible dark "island"
  // ink -- so this is a pure opt-in and legacy prefs are unaffected.
  themeModal: '--player-modal',

  // Active-tab pill (4.1.0-beta.6). The content tabs (Transcript, Documents,
  // Agenda) ship as bare labels with an underline under the active one. Two
  // clients restyle that active tab as a FILLED PILL -- CVTV a purple
  // rounded-rect, TVW a white full-pill inside a dark strip -- which the
  // shadow boundary otherwise retires. Same presence-means-filled contract as
  // the button: `themeTabActiveBg` carrying a value is what makes the tab a
  // pill, and the player collapses the underline into the fill so a filled
  // pill does not draw a stray line beneath it.
  themeTabActiveBg: '--player-tab-active-bg',
  themeTabActiveText: '--player-tab-active-color',
  themeTabActiveRadius: '--player-tab-active-radius',
  // The line beneath the active tab. The player collapses it INTO the fill
  // when themeTabActiveBg is set -- correct for CVTV's purple pill, which has
  // no line, and the reason TVW's white pill carrying a #4bafff line was the
  // one measured brand element round 2 could not reproduce. The custom
  // property already existed and already won that cascade; nothing reached it
  // from a preference key. This is the key. hosted-player #822.
  themeTabActiveIndicator: '--player-tab-active-indicator',
  themeTabStripBg: '--player-tab-strip-bg',

  // Round 2 (4.1.0-beta.7). Driven by CVTV, who could not express their
  // production layout through the Builder. The player-side set was validated
  // against their live saved theme on clones of their own site before any of
  // it reached the schema -- see hosted-player `theming-tokens-round-2.md`.
  //
  // Lists: ONE group drives documents, agenda AND transcript. They are the
  // same visual object, and handling them as three parallel sets is exactly
  // what let a player rule silently cover two of the three during validation.
  themeListRowBg: '--player-list-row-bg',
  themeListRowBgAlt: '--player-list-row-bg-alt',
  themeListRowColor: '--player-list-row-color',
  themeListRowDivider: '--player-list-row-divider',

  // Transcript is the ONE list surface a client genuinely styles differently
  // (CVTV run theirs flat and white while documents and agenda are striped and
  // sage). The player chains each of these to its themeListRow* counterpart, so
  // setting the base group alone still styles all three consistently. Documents
  // and agenda get no override group by design -- they have never diverged.
  themeTranscriptRowBg: '--player-transcript-row-stripe-bg',
  themeTranscriptRowBgAlt: '--player-transcript-row-stripe-bg-alt',
  themeTranscriptRowColor: '--player-transcript-row-color',
  themeTranscriptRowDivider: '--player-transcript-row-divider',

  // Unselected tab chrome. beta.6 tokenised the ACTIVE tab only, leaving a
  // boxed-tab brand unreachable. themeTabBorderWidth defaults to 0 in the
  // player -- a transparent 1px border would grow every client's tabs by 2px --
  // so the Builder writes it alongside themeTabBorder; a colour alone is inert.
  themeTabBg: '--player-tab-bg',
  themeTabColor: '--player-tab-color',
  themeTabBorder: '--player-tab-border',
  themeTabBorderWidth: '--player-tab-border-width',
  themeTabRadius: '--player-tab-radius',
  themeTabGap: '--player-tab-gap',

  // Tab strip + panel. themeTabActiveBorder keeps the active tab's box model
  // identical to its neighbours once they gain an outline, so it does not shift
  // sideways. The paddings are the inset of the tabs within the strip and the
  // air below it -- the panel carried none, so lists sat flush against the strip.
  themeTabActiveBorder: '--player-tab-active-border',
  themeTabStripBorder: '--player-tab-strip-border',
  themeTabStripPadding: '--player-tab-strip-padding',
  themeTabPanelPadding: '--player-tab-panel-padding',

  // Interaction. themeTabHoverStrength is a PERCENTAGE, not a colour: the
  // player overlays its tint (black on light themes, white on dark) at that
  // strength, so one number reads as a darken or a lighten correctly without a
  // theme branch. themeFocusRing is a WCAG 2.4.11 lever -- the ring is
  // otherwise the accent, which can be low-contrast on a client's own ground.
  themeTabHoverStrength: '--player-tab-hover-strength',
  themeFocusRing: '--player-focus-ring',

  // Category label + event date.
  themeCategoryBg: '--player-category-bg',
  themeCategoryBorder: '--player-category-border',
  themeUnderlayDateColor: '--player-underlay-date-color',

  // Transcript toolbar toggles (Highlight / Follow). Empty keeps the literal
  // yellow/sky marker tints the player ships; a client whose accent drove this
  // surface in v3 PRODUCTION sets these to get it back. Colour travels with the
  // fill because the shipped ink is pinned dark against a fixed light fill.
  themeHighlightToggleBg: '--player-highlight-toggle-bg',
  themeHighlightToggleBorder: '--player-highlight-toggle-border',
  themeHighlightToggleColor: '--player-highlight-toggle-color',
  themeFollowToggleBg: '--player-follow-toggle-bg',
  themeFollowToggleBorder: '--player-follow-toggle-border',
  themeFollowToggleColor: '--player-follow-toggle-color',
};

/** The theme keys, in Builder display order. */
export const THEME_KEYS = Object.keys(THEME_TOKEN_MAP);

/**
 * Modal ink mirror -- the editor-side twin of the player's MODAL_INK_MIRROR
 * (hosted-player `src/app/lib/theme-tokens.ts`).
 *
 * The player's dialog is a light-island floor: the card is white by default,
 * so `dialog.player-dialog` re-pins its ink/tint/border to fixed dark
 * constants via `--player-modal-*` hooks that are ABSENT by default. When
 * `themeModal` themes the card dark, those hooks must instead carry the
 * client's themed (light) palette, or the dark constants paint dark-on-dark
 * (a `#262626` title on a `#262626` card is invisible). The player derives
 * this in `applyThemeTokens`; the preview must derive the SAME hooks or it
 * lies -- painting `--player-modal` alone flips the card dark but leaves the
 * ink on the dark island floor. Keep in lockstep with the player's map.
 */
export const MODAL_INK_MIRROR = {
  themeTextStrong: '--player-modal-text-strong',
  themeText: '--player-modal-text',
  themeTextMuted: '--player-modal-text-muted',
  themeTextSoft: '--player-modal-text-soft',
  themeTint: '--player-modal-tint',
  themeBorder: '--player-modal-border',
};

/**
 * How the Builder groups the tokens: like with like, each group separated
 * by an <hr> so a related set (all the text colors, both border levers,
 * the three button controls) reads as one decision rather than loose
 * dials.
 *
 * This is presentation only -- every key still comes from the metaMap
 * through PreferenceInput, so the parity test covers them all. Any theme
 * key NOT listed here still renders: ThemeTab appends the leftovers as a
 * final unlabelled group, so a future PMM addition is shown, not dropped.
 */
/**
 * The Tabs sub-sections.
 *
 * Seventeen of the 52 keys are tab keys. Shipped as four peer groups (active,
 * unselected, strip, hover) they read as four unrelated sections, and a user
 * looking for "the tabs" had to recognise that all four were the same thing.
 * They are one surface with four parts, so they are one group with four
 * sub-sections.
 *
 * `themeTabActiveBorder` sits with Active rather than with the strip, where
 * it shipped: it paints the active tab, not the strip around it.
 */
const TAB_SUBGROUPS = [
  {
    // Its own section because it is the one tab lever that is NOT about the
    // active/unselected split -- `--player-tab-size` is on `[part~="tab"]`, so
    // it sizes every tab and the tab icons with it. Filing it under either
    // half would say it applies to that half only.
    key: 'tabs.all',
    title: 'All tabs',
    keys: ['themeTabFontSize'],
  },
  {
    key: 'tabs.active',
    title: 'Active tab',
    keys: [
      'themeTabActiveBg',
      'themeTabActiveIndicator',
      'themeTabActiveText',
      'themeTabActiveRadius',
      'themeTabActiveBorder',
    ],
  },
  {
    key: 'tabs.unselected',
    title: 'Unselected tabs',
    keys: [
      'themeTabBg',
      'themeTabColor',
      'themeTabBorder',
      'themeTabBorderWidth',
      'themeTabRadius',
      'themeTabGap',
    ],
  },
  {
    key: 'tabs.strip',
    title: 'Strip and panel',
    keys: [
      'themeTabStripBg',
      'themeTabStripBorder',
      'themeTabStripPadding',
      'themeTabPanelPadding',
    ],
  },
  {
    key: 'tabs.hover',
    title: 'Hover',
    keys: ['themeTabHoverStrength'],
  },
];

/** A parent group's own key list is its sub-sections', so the two cannot drift. */
function keysOf (subGroups) {
  return subGroups.reduce((all, sub) => all.concat(sub.keys), []);
}

/**
 * The Builder's layout, in two tiers.
 *
 * `tier: 'essential'` is the block that opens with the screen and is never
 * collapsed -- the seven levers that land most brands. Everything else is
 * `fineTune`: accordion groups, closed by default, organised by the SURFACE
 * they paint rather than by the kind of lever they are.
 *
 * The tiering is the answer to the count problem. 52 controls presented at
 * one altitude is the thing that caused analysis paralysis; 7 plus an opt-in
 * is the same set with an entry point.
 *
 * Two nesting mechanisms live here and mean different things:
 *
 *   `subGroups` -- visual sub-sections of one surface (the four parts of
 *   Tabs). Presentation only, no behaviour attached.
 *
 *   `scopeOf` + `scopeLabel` -- a NARROWED VARIANT of another group: the same
 *   levers aimed at one surface (transcript rows vs. list rows generally).
 *   Carries a rule the rebuild must preserve -- the disclosure opens whenever
 *   any key in the scope already has a value, so an existing override is
 *   never hidden behind a closed heading.
 *
 * Conflating them would break the transcript disclosure.
 */
export const THEME_GROUPS = [
  {
    key: 'essentials',
    title: 'Essentials',
    tier: 'essential',
    keys: [
      'themeAccent',
      'themeTextStrong',
      'themeText',
      'themeTextMuted',
      'themeBorder',
      'themeRadius',
      'themeFontFamily',
    ],
  },
  {
    // The rest of the palette. Not Essential because none of the three is a
    // lever a brand is likely to reach for first: tint is an overlay
    // multiplier, soft text is the fourth ink level, and the focus ring
    // already follows the accent unless deliberately separated.
    key: 'paletteExtras',
    title: 'More palette',
    tier: 'fineTune',
    keys: ['themeTint', 'themeTextSoft', 'themeFocusRing'],
  },
  {
    key: 'tabs',
    title: 'Tabs',
    tier: 'fineTune',
    subGroups: TAB_SUBGROUPS,
    keys: keysOf(TAB_SUBGROUPS),
  },
  {
    key: 'lists',
    title: 'Lists',
    tier: 'fineTune',
    keys: [
      'themeListRowBg',
      'themeListRowBgAlt',
      'themeListRowColor',
      'themeListRowDivider',
      // Sizes all three lists at once, like every other key in this group.
      // There is deliberately no transcript-scoped counterpart: the player
      // drives label and meta from this ONE value so a row stays internally
      // proportional, and a per-surface size would reintroduce the split that
      // caused the complaint.
      'themeListRowFontSize',
    ],
  },
  {
    // A SCOPED group: the same four levers as `lists`, aimed at one surface.
    //
    // Transcript is the only scope today because it is the only list surface
    // a client has actually diverged on -- CVTV run theirs flat and white
    // while documents and agenda are striped and sage. If documents or agenda
    // ever need to differ, it is another entry here plus PMM keys; no
    // component changes.
    key: 'lists.transcript',
    title: 'Transcript list',
    tier: 'fineTune',
    scopeOf: 'lists',
    scopeLabel: 'Transcript',
    keys: [
      'themeTranscriptRowBg',
      'themeTranscriptRowBgAlt',
      'themeTranscriptRowColor',
      'themeTranscriptRowDivider',
    ],
  },
  {
    // The block above the player's tab strip: title, description, date and
    // category chips. The two font sizes used to sit with the text COLOURS,
    // which grouped them by the kind of lever they are rather than by what
    // they paint -- and left "Text and font" holding seven controls that
    // spanned three surfaces.
    key: 'eventHeader',
    title: 'Event header',
    tier: 'fineTune',
    keys: [
      'themeTitleFontSize',
      'themeBodyFontSize',
      'themeDateFontSize',
      'themeUnderlayDateColor',
      'themeCategoryBg',
      'themeCategoryBorder',
    ],
  },
  {
    key: 'buttons',
    title: 'Buttons',
    tier: 'fineTune',
    keys: ['themeButtonBg', 'themeButtonText', 'themeButtonSize'],
  },
  {
    key: 'transcriptToggles',
    title: 'Transcript toolbar',
    tier: 'fineTune',
    keys: [
      'themeHighlightToggleBg',
      'themeHighlightToggleBorder',
      'themeHighlightToggleColor',
      'themeFollowToggleBg',
      'themeFollowToggleBorder',
      'themeFollowToggleColor',
    ],
  },
  {
    key: 'surface',
    title: 'Dialogs',
    tier: 'fineTune',
    keys: ['themeModal'],
  },
];

export const SCOPED_GROUPS = THEME_GROUPS.filter(g => g.scopeOf);

/**
 * The player's shipped CSS defaults, for DISPLAY ONLY -- what a color
 * input shows in its swatch while the stored value is still ''. These
 * are never written to a preference; storing one would turn an unthemed
 * pref into a themed one and opt it out of fill-only fan-out.
 *
 * Source: hosted-player `src/app/styles/global.css`. The accent is
 * `oklch(62.3% 0.214 259.815)` there; its sRGB equivalent is used here
 * because `<input type="color">` only accepts hex.
 */
export const DEFAULT_SWATCHES = {
  themeAccent: '#3b82f6',
  themeTextStrong: '#262626',
  themeText: '#404040',
  themeTextMuted: '#737373',
  themeTextSoft: '#a3a3a3',
  themeTint: '#000000',
  themeBorder: '#d4d4d4',
  themeFontFamily: '',
  themeRadius: '6px',

  // Structural defaults, from the player's own fallbacks: the action button
  // ships ghost (no fill) at 0.75rem/12px, body copy at 0.75rem/12px, and the
  // underlay title at 0.875rem/14px.
  themeButtonBg: 'transparent',
  themeButtonText: '#737373',
  themeButtonSize: '12px',
  themeBodyFontSize: '12px',
  themeTitleFontSize: '14px',

  // The rest of the type scale (4.1.0), from the player's own fallbacks: the
  // event date and the tab labels at 0.75rem/12px, list rows at 0.875rem/14px.
  themeDateFontSize: '12px',
  themeTabFontSize: '12px',
  themeListRowFontSize: '14px',

  // The dialog card ships white (`--color-player-modal` = #ffffff). Shown
  // as the swatch while unset; never written unless the client opts in.
  themeModal: '#ffffff',

  // The active tab ships unfilled and square, with its label on the strong
  // text color and the strip itself transparent -- i.e. today's underlined
  // tab. Display only; setting any of them is the opt-in to the pill.
  themeTabActiveBg: 'transparent',
  // Blank follows the fill, then the active outline, then body text -- so with
  // nothing set the underline paints #404040. A literal is only the floor
  // here; RESOLVED_FROM walks the real chain for the ghosted value.
  themeTabActiveIndicator: '#404040',
  themeTabActiveText: '#262626',
  themeTabActiveRadius: '0px',
  themeTabStripBg: 'transparent',

  // Round 2. All unset by default: lists ship unstriped with a hairline
  // divider, tabs ship as bare labels with no fill/outline/radius, the tab
  // strip and panel carry no padding, hover is unchanged, the focus ring is
  // the accent, the category tag is an outlined pill, and the toolbar toggles
  // ship their literal marker tints.
  themeListRowBg: 'transparent',
  themeListRowBgAlt: 'transparent',
  themeListRowColor: '#404040',
  themeListRowDivider: '#e5e5e5',
  themeTranscriptRowBg: 'transparent',
  themeTranscriptRowBgAlt: 'transparent',
  // Muted, not body. A transcript cue that is not the current one is
  // `text-player-text-muted` -- the player mutes the others so the current
  // one reads as current. The chain in RESOLVED_FROM already resolved this
  // correctly; this literal (the fallback when the chain cannot compute) was
  // still the old wrong answer, so the two disagreed.
  themeTranscriptRowColor: '#737373',
  themeTranscriptRowDivider: '#e5e5e5',
  themeTabBg: 'transparent',
  themeTabColor: '#737373',
  themeTabBorder: 'transparent',
  themeTabBorderWidth: '0px',
  themeTabRadius: '0px',
  themeTabGap: '0px',
  themeTabActiveBorder: 'transparent',
  themeTabStripBorder: '#e5e5e5',
  themeTabStripPadding: '',
  themeTabPanelPadding: '',
  themeTabHoverStrength: '0%',
  themeFocusRing: '#3b82f6',
  themeCategoryBg: 'transparent',
  themeCategoryBorder: '#d4d4d4',
  themeUnderlayDateColor: '#737373',
  themeHighlightToggleBg: '#fef9c3',
  themeHighlightToggleBorder: '#facc15',
  themeHighlightToggleColor: '#171717',
  themeFollowToggleBg: '#e0f2fe',
  themeFollowToggleBorder: '#38bdf8',
  themeFollowToggleColor: '#171717',
};

/**
 * Font stacks offered by the Builder. The player does not load web
 * fonts -- the hosting page must already serve them -- so anything
 * beyond the system stack is a request, not a guarantee.
 */
/**
 * The tail the player itself uses when `--player-font-family` is unset
 * (`global.css:92`). Kept identical on purpose: a client picking a font should
 * fall back to exactly what they would have had by picking none.
 *
 * The emoji families are the load-bearing part. Setting the token REPLACES the
 * whole `var()` fallback, so any theme that named a font lost them -- which is
 * how a themed player ends up rendering tofu where an unthemed one renders an
 * emoji. That applied to our own shipped options too, not just hand-typed
 * stacks.
 */
const EMOJI_TAIL = '"Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji"';

const GENERIC_TAIL = {
  'sans-serif': 'ui-sans-serif, system-ui, sans-serif',
  serif: 'ui-serif, Georgia, serif',
  monospace: 'ui-monospace, SFMono-Regular, monospace',
};

const GENERICS = Object.keys(GENERIC_TAIL);

/** The families in a stack, trimmed, as written. */
function splitStack (stack) {
  return String(stack || '')
    .split(',')
    .map(part => part.trim())
    .filter(Boolean);
}

/**
 * Append the system fallback a client cannot be expected to remember.
 *
 * A stack of one name is a cliff: if the font does not load -- and the player
 * never loads it, the hosting page does -- the browser drops to its own
 * default, which on many setups is a serif nobody chose. This guarantees a
 * sane landing and restores the emoji families the token would otherwise
 * displace.
 *
 * The generic the client wrote is honoured, so a serif choice falls back to
 * serif rather than being quietly turned sans. Idempotent: appending twice
 * cannot double the tail, which matters because every stored value passes
 * through here on its way in.
 */
export function withSystemFallback (stack) {
  const families = splitStack(stack);
  if (!families.length) return '';

  // The client's own generic, if they named one, decides the tail.
  const generic = families.filter(f => GENERICS.indexOf(f.toLowerCase()) !== -1).pop();
  const tail = GENERIC_TAIL[(generic || 'sans-serif').toLowerCase()];

  // Drop anything already in the tail so re-normalising is a no-op.
  const tailParts = splitStack(tail + ', ' + EMOJI_TAIL).map(f => f.toLowerCase());
  const head = families.filter(f => tailParts.indexOf(f.toLowerCase()) === -1);
  if (!head.length) return '';

  return head.join(', ') + ', ' + tail + ', ' + EMOJI_TAIL;
}

/** One named family plus the full fallback. The only way stacks are built. */
export function fontStack (family, generic) {
  return withSystemFallback("'" + family + "', " + (generic || 'sans-serif'));
}

/** The families a client actually chose, with the boilerplate tail removed. */
export function stripSystemFallback (stack) {
  const families = splitStack(stack);
  const tailParts = splitStack(
    GENERIC_TAIL['sans-serif'] + ', ' + GENERIC_TAIL.serif + ', ' +
    GENERIC_TAIL.monospace + ', ' + EMOJI_TAIL
  ).map(f => f.toLowerCase());
  return families.filter(f => tailParts.indexOf(f.toLowerCase()) === -1).join(', ');
}

/**
 * The fonts the Theme Builder offers, and the ONLY place their stacks are
 * written -- presets compose the same `fontStack()` so the two cannot drift
 * and `matchPreset` keeps working.
 */
export const FONT_OPTIONS = [
  { value: '', label: 'Player default (system)' },
  { value: fontStack('Inter'), label: 'Inter' },
  { value: fontStack('DM Sans'), label: 'DM Sans' },
  { value: fontStack('Source Sans 3'), label: 'Source Sans' },
  { value: fontStack('Georgia', 'serif'), label: 'Georgia (serif)' },
  { value: fontStack('JetBrains Mono', 'monospace'), label: 'JetBrains Mono' },
];


/**
 * Starting points, not destinations -- every preset is hand-tunable
 * afterward. `Default` is the un-theming gesture: it clears every key
 * back to '' so the player's own look returns.
 *
 * A preset omitting a key means that key is cleared, so applying a
 * preset always yields a complete, self-consistent look rather than a
 * blend with whatever was set before.
 *
 * A PRESET IS AN AUDIENCE, NOT A PALETTE
 * These were authored against the 9-key palette set, so they only ever
 * differed by colour. At 52 keys that is a fifth of what a look actually is,
 * and the more consequential axis turned out to be SIZE: the player ships
 * 12px body copy and a 14px title, which is small for a public body whose
 * viewers are reading a transcript of a statutory proceeding. Government and
 * Education therefore run a size up, Accessible and Dark Large run two, and
 * Modern takes a position on tab shape.
 *
 * Every colour pair a preset introduces is checked against `contrastRatio`
 * before it ships -- two of the originals did not clear AA and were corrected
 * in the process (see Education and Modern below).
 *
 * The dropdown can only show five palette dots, so a preset that differs by
 * size looks identical to one that does not. That is what the `description`
 * is carrying -- it names the non-colour half.
 */
export const PRESETS = [
  {
    name: 'Player Default',
    description: 'Clear all theming',
    values: {},
  },
  {
    // The canonical dark recipe from the theming-tool design doc,
    // verified against the real TVW clone: chrome text flips to
    // rgb(245,245,245) while the surface stays transparent, so the
    // client's own dark page shows through instead of a light card.
    name: 'Dark',
    description: 'For dark-background sites',
    values: {
      themeAccent: '#8ab4f8',
      themeTextStrong: '#f5f5f5',
      themeText: '#e5e5e5',
      themeTextMuted: '#b5b5b5',
      themeTextSoft: '#8f8f8f',
      themeTint: '#ffffff',
      themeBorder: '#5a5a5a',
      // A dark dialog card so the Share/Download surfaces carry the light
      // text above instead of showing it on a white card. Lifted slightly
      // off a near-black page so the card still reads as a distinct surface.
      themeModal: '#262626',
    },
  },
  {
    // Public meetings, watched by the general public on their own time.
    // Formal palette, and type a size up from the player's defaults: this
    // audience skews older and is reading a transcript, not skimming a feed.
    name: 'Government',
    description: 'Formal navy, larger type',
    values: {
      themeAccent: '#1e3a5f',
      themeTextStrong: '#1a2433',
      themeText: '#2f3a47',
      themeTextMuted: '#5b6673',
      themeTextSoft: '#6c757d',
      themeBorder: '#c5c5c5',
      themeFontFamily: fontStack('Source Sans 3'),
      themeRadius: '4px',
      themeBodyFontSize: '15px',
      themeTitleFontSize: '20px',
      themeButtonSize: '14px',
    },
  },
  {
    // Classrooms and lecture capture, often projected. The largest type of
    // the light presets, and the friendliest shape.
    //
    // The accent is teal-700, not the teal-600 this shipped with: teal-600
    // measured 3.74:1 on white, and the accent paints LINK text, so the
    // preset was shipping a link colour below AA.
    name: 'Education',
    description: 'Warm teal, large friendly type',
    values: {
      themeAccent: '#0f766e',
      themeTextStrong: '#1a2e35',
      themeText: '#33454b',
      themeTextMuted: '#5f7378',
      themeTextSoft: '#6c757d',
      themeBorder: '#99d6cd',
      themeFontFamily: fontStack('DM Sans'),
      themeRadius: '8px',
      themeBodyFontSize: '16px',
      themeTitleFontSize: '22px',
      themeButtonSize: '14px',
    },
  },
  {
    // Brand-forward orgs. The one preset that takes a position on tab SHAPE:
    // a filled pill rather than the shipped underline.
    //
    // indigo-600 for the pill, not the indigo-500 this shipped with. White on
    // indigo-500 measures 4.47:1 -- so the preset would have tripped the
    // Builder's own tab-contrast warning the moment it was applied.
    name: 'Modern',
    description: 'Rounded and vivid, filled tab pill',
    values: {
      themeAccent: '#4f46e5',
      themeTextStrong: '#18181b',
      themeText: '#3f3f46',
      themeTextMuted: '#71717a',
      themeTextSoft: '#767676',
      themeBorder: '#e4e4e7',
      themeFontFamily: fontStack('Inter'),
      themeRadius: '10px',
      themeTabActiveBg: '#4f46e5',
      themeTabActiveText: '#ffffff',
      themeTabActiveRadius: '20px',
      themeBodyFontSize: '13px',
      themeTitleFontSize: '18px',
    },
  },
  {
    // Editorial and civic sites with their own strong typography. Squares
    // everything off and boxes the tabs -- the one preset that exercises the
    // outline pairing, so it writes both halves explicitly.
    name: 'Sharp',
    description: 'Square corners, boxed tabs',
    values: {
      themeAccent: '#000000',
      themeTextStrong: '#000000',
      themeText: '#333333',
      themeTextMuted: '#666666',
      themeTextSoft: '#767676',
      themeBorder: '#cccccc',
      themeRadius: '0px',
      themeTabBorder: '#cccccc',
      themeTabBorderWidth: '1px',
      themeTabRadius: '0px',
      themeTitleFontSize: '18px',
    },
  },
  {
    // Courts, clerks and the written record. The only serif preset -- Georgia
    // was in FONT_OPTIONS from the start and no preset had ever used it.
    //
    // Serif brings the size requirement with it: a serif face at the player's
    // shipped 12px body is markedly harder to read than a sans at the same
    // size, because the modulation and the small counters need the pixels.
    // This is the same argument as Government and Education, one step further.
    //
    // NAMED FOR THE LOOK, NOT THE AUDIENCE, and not "Record": recording is an
    // action in this product, so a preset by that name reads as a verb to
    // anyone new to streaming. "Legal" is worse -- there is a `legal`
    // preference labelled "Legal Notice" and a "Legal & Contact" section in
    // the same editor. "Courts" is clean but narrows it: a council whose look
    // this suits would skip it. "Formal" would fight Government's own
    // description, which already reads "Formal navy".
    name: 'Classic',
    description: 'Serif type, sized for long proceedings',
    values: {
      themeAccent: '#7a2e2e',
      themeTextStrong: '#1a1a1a',
      themeText: '#2e2e2e',
      themeTextMuted: '#5c5c5c',
      themeTextSoft: '#767676',
      themeBorder: '#c8c2b6',
      themeFontFamily: fontStack('Georgia', 'serif'),
      // Nearly square. A record should not look like a product.
      themeRadius: '2px',
      themeBodyFontSize: '17px',
      themeTitleFontSize: '26px',
      themeButtonSize: '15px',
    },
  },
  {
    // The accessibility-first option, and the reason presets are worth
    // re-authoring at all: the player's shipped body size is 12px, which is
    // small for a public body streaming statutory proceedings. Every pair
    // here clears AA against white -- muted text at 8.86:1, the accent at
    // 5.99:1, and even the border at 4.54:1, so dividers are visible to a
    // low-vision viewer rather than merely tasteful.
    //
    // The focus ring is deliberately left blank so it follows the accent:
    // a strong blue ring is the convention viewers already recognise, and
    // inventing a second high-visibility colour here would be taste, not
    // accessibility.
    name: 'Accessible',
    description: 'Maximum contrast, large type',
    values: {
      themeAccent: '#0b5cd5',
      themeTextStrong: '#000000',
      themeText: '#1a1a1a',
      themeTextMuted: '#4a4a4a',
      themeTextSoft: '#5f5f5f',
      themeBorder: '#767676',
      themeRadius: '4px',
      themeBodyFontSize: '17px',
      themeTitleFontSize: '26px',
      themeButtonSize: '16px',
    },
  },
  {
    // Lobby screens, overflow rooms, anything projected and read from across
    // a room. Dark's palette at Accessible's type sizes -- the two axes are
    // independent, which is the argument for presets carrying size at all.
    name: 'Dark Large',
    description: 'Dark surface, large type for screens read at a distance',
    values: {
      themeAccent: '#8ab4f8',
      themeTextStrong: '#ffffff',
      themeText: '#ececec',
      themeTextMuted: '#c4c4c4',
      themeTextSoft: '#9a9a9a',
      themeTint: '#ffffff',
      themeBorder: '#5a5a5a',
      themeModal: '#262626',
      themeBodyFontSize: '17px',
      themeTitleFontSize: '26px',
      themeButtonSize: '16px',
    },
  },
];

// ----- SDK -----

/**
 * True when any theme key carries a value.
 *
 * This is the fill-only fan-out predicate: a preference this returns
 * false for is "unthemed" and may be filled; one it returns true for
 * carries a deliberate look and must never be overwritten.
 *
 * Tolerates a partial bag (a legacy row read before the 4.1 defaults
 * were applied), so a missing key reads as unset rather than throwing.
 */
export function hasTheme (properties) {
  if (!properties) return false;
  return THEME_KEYS.some(key => {
    const value = properties[key];
    return typeof value === 'string' && value !== '';
  });
}

/** Every key as a flat bag, present, unset ones as ''. */
export function extractTheme (properties) {
  const theme = {};
  THEME_KEYS.forEach(key => {
    const value = properties ? properties[key] : '';
    theme[key] = typeof value === 'string' ? value : '';
  });
  return theme;
}

/**
 * A complete theme bag for a preset: its values over a cleared base, so
 * applying one never leaves a stale token behind from the previous look.
 */
export function presetValues (preset) {
  const cleared = {};
  THEME_KEYS.forEach(key => { cleared[key] = ''; });
  return { ...cleared, ...(preset && preset.values) };
}

/**
 * Which preset the current values correspond to, by name, or null when
 * they match none of them.
 *
 * Derived rather than remembered: a stored "selected preset" would go
 * stale the moment someone edited a single color, and would survive a
 * reload claiming a look the values no longer have. Comparing the values
 * themselves cannot drift.
 *
 * Returns 'Player Default' for a fully unset theme, which is the case
 * worth showing most -- it is otherwise indistinguishable from "nobody
 * has chosen anything yet".
 *
 * Null is meaningful: it means hand-tuned, and the UI should show no
 * preset as current rather than guess at the nearest one.
 *
 * Hex is compared case-insensitively, so `#FFFFFF` typed by hand still
 * matches a preset's `#ffffff`.
 */
export function matchPreset (properties) {
  const theme = extractTheme(properties);
  const found = PRESETS.find(preset => {
    const values = presetValues(preset);
    return THEME_KEYS.every(key => norm(values[key]) === norm(theme[key]));
  });
  return found ? found.name : null;
}

function norm (value) {
  return typeof value === 'string' ? value.trim().toLowerCase() : '';
}

// ----- Contrast (WCAG) -----
//
// D-c contrast ownership: the player only paints; Control Center owns
// keeping a client-set background legible against the ink on it. These are
// the token pairs that can be individually valid yet jointly unreadable --
// a dark `themeModal` with dark (or unset) text, a dark `themeTabActiveBg`
// with the dark strong-text label -- so the Builder checks them and offers
// a fix rather than shipping the half-configured-theme bug.

/**
 * The dark "island" ink the player falls back to INSIDE a dialog when a
 * text token is unset. Mirrors hosted-player `global.css`
 * (`dialog.player-dialog`): `--color-player-text*: var(--player-modal-text*,
 * <this default>)`. So when `themeModal` is set but a text token is blank,
 * the dialog paints this dark ink -- which is exactly the illegible case on
 * a dark card. Kept in lockstep with the player's dialog defaults.
 */
/**
 * The derivation graph: which SETTABLE token a blank field actually falls
 * back to, and how.
 *
 * This is the most consequential thing in the Builder. Nineteen of the 52
 * keys do not fall back to a constant -- they fall back to another token the
 * user can set, so what a blank field paints is a function of the rest of the
 * theme. Set Muted Text to `#b5b5b5` and a blank Unselected Tab Label now
 * paints `#b5b5b5`. The user is not choosing "no opinion"; they are choosing
 * a specific colour they cannot see. That is exactly how CVTV's hydration
 * produced grey tabs against an intended white -- measured
 * `rgb(181, 181, 181)`.
 *
 * `DEFAULT_SWATCHES` alone cannot express this: it is a table of fixed
 * literals, so it shows the right answer only for an unthemed preference and
 * lies for every themed one. This table holds the EDGES instead of the
 * values, so the resolution follows the theme.
 *
 * Each edge cites the rule in the player's `global.css` that creates it.
 * They are the one part of this file that can drift silently if the player
 * changes a fallback, which is the cost of the editor resolving them rather
 * than asking the player -- the player's fallbacks live inside its shadow
 * root, where a same-origin parent still cannot read them.
 *
 *   `via: 'mix'` -- the CSS is `color-mix(in srgb, <token>, white 30%)`.
 *
 *   `from: [a, b, c]` -- a CHAIN, mirroring a nested `var()` fallback in the
 *   player. The first link the user has actually SET wins; if none is set the
 *   last link resolves, because it is the one whose own default terminates the
 *   chain. Intermediate links are never read from `DEFAULT_SWATCHES`: an unset
 *   custom property falls THROUGH in CSS, so treating its display swatch as a
 *   value would stop the walk one link early and ghost `transparent`.
 *
 * The chains are verified against the player's generated artifact rather than
 * read out of `global.css` by eye -- `theme-defaults.json` publishes
 * `derivesFrom` per key, and these two match it exactly.
 */
export const RESOLVED_FROM = {
  themeButtonText: { from: 'themeTextMuted' },              // global.css:272
  themeTabActiveText: { from: 'themeTextStrong' },          // global.css:245
  // Two chains, both confirmed against theme-defaults.json `derivesFrom`.
  //
  // The indicator is the line under the active tab; the player collapses it
  // into the fill, then into the active outline, then lands on body text.
  themeTabActiveIndicator: {
    from: ['themeTabActiveBg', 'themeTabActiveBorder', 'themeText'],
  },                                                        // global.css:484-489
  // This one had NO edge at all and ghosted `transparent` from the swatch
  // table while the player painted rgb(64, 64, 64) -- the rail was showing a
  // blank field as invisible when it renders as a dark line.
  themeTabActiveBorder: { from: ['themeTabActiveBg', 'themeText'] }, // global.css:534
  themeTabColor: { from: 'themeTextMuted' },                // global.css:392
  themeUnderlayDateColor: { from: 'themeTextMuted' },       // global.css:569
  themeFocusRing: { from: 'themeAccent' },                  // global.css:446-447
  themeCategoryBorder: { from: 'themeBorder' },             // global.css:539
  // `revert-layer` rolls back to the component's own Tailwind utility, and
  // the three list surfaces do not share one. Documents and agenda label rows
  // `text-player-text` (DocumentListing.tsx:74, agenda item.tsx:67), which is
  // what this edge reports. Transcript is the exception -- see below.
  themeListRowColor: { from: 'themeText' },                 // global.css:361-364
  themeListRowDivider: { from: 'themeBorder', via: 'mix' }, // global.css:378 + :80
  themeTabStripBorder: { from: 'themeBorder', via: 'mix' }, // global.css:487, :495
  themeTranscriptRowBg: { from: 'themeListRowBg' },         // global.css:338
  themeTranscriptRowBgAlt: { from: 'themeListRowBgAlt' },   // global.css:341
  // NOT themeText, and not themeListRowColor's terminal either. A transcript
  // cue that is not the current one is `text-player-text-muted`
  // (TranscriptList.tsx:107) -- the player mutes the others so the current cue
  // reads as current. So a blank Transcript Row Text paints MUTED text, and
  // changing Muted Text Color restyles the whole transcript.
  //
  // This edge was previously recorded as chaining to themeText, which is what
  // the cross-repo table still says. It is wrong, and wrong in the exact way
  // this resolver exists to prevent: the rail would have ghosted the body
  // colour for a field the player paints muted.
  //
  // The current cue is `text-player-text-strong`, so one ghosted value cannot
  // describe both states. Muted is the one that covers nearly every row on
  // screen, so it is the one shown.
  themeTranscriptRowColor: { from: 'themeTextMuted' },      // global.css:366-367
  themeTranscriptRowDivider: { from: 'themeListRowDivider' }, // global.css:384
};

/**
 * Keys whose BLANK value differs by surface, and cannot honestly show one swatch.
 *
 * `revert-layer` rolls a token back to the component's own Tailwind utility,
 * and the three list surfaces do not share one. So a single ghosted colour is
 * not merely imprecise here -- it is wrong for at least one surface the control
 * claims to drive. `themeListRowColor` says "all three lists" and then ghosts
 * the colour only two of them use.
 *
 * SCOPE. The player reports eight `revert-layer` terminals, but six of them --
 * the Highlight and Follow toggle triples -- resolve on ONE surface
 * (`transcript-toggle`) to ONE value each. Their caveat is a STATE, not a
 * surface ("while the toggle is on"), and their descriptions already carry it.
 * Only these two vary by where they paint, so only these two need this.
 *
 * Values are edges, not literals, for the same reason `RESOLVED_FROM` holds
 * edges: each surface's blank follows a settable token, so the disclosed range
 * has to move with the theme. Mirrored from the player's generated
 * `theme-defaults.json` `surfaces[]` -- documents/agenda `text-player-text`,
 * a transcript cue `text-player-text-muted`, the current cue
 * `text-player-text-strong`.
 */
export const SURFACE_DEFAULTS = {
  themeListRowColor: {
    note: 'Until you set a color, each list uses its own default:',
    surfaces: [
      { label: 'Documents and agenda rows', from: 'themeText' },
      { label: 'Transcript, current line', from: 'themeTextStrong' },
      { label: 'Transcript, other lines', from: 'themeTextMuted' },
    ],
  },
  themeTranscriptRowColor: {
    note: 'Until you set a color, transcript lines use two different defaults:',
    surfaces: [
      { label: 'The line currently playing', from: 'themeTextStrong' },
      { label: 'Every other line', from: 'themeTextMuted' },
    ],
  },
};

/**
 * What a blank control paints on each surface it drives, resolved against the
 * theme being edited.
 *
 * Returns null for the 50 keys with a single answer -- the ordinary ghosted
 * swatch is honest for those. Only meaningful while the control is UNSET: once
 * it carries a value, that value paints on every surface and the variance is
 * gone.
 *
 * @param {string} key
 * @param {object} properties  the preference being edited (merged edits)
 * @returns {Array<{label: string, value: string}>|null}
 */
export function surfaceVariance (key, properties) {
  const spec = SURFACE_DEFAULTS[key];
  if (!spec) return null;
  if (properties && properties[key]) return null;

  const props = properties || {};
  // The swatch above the note already shows ONE of these -- whichever the
  // control's own resolver picked. Marking it is what stops the note reading as
  // a contradiction: the field says `#404040 DEFAULT`, so a note that opens
  // with "blank" invites "but it is not blank". Computed rather than hardcoded,
  // so it still points at the right row when the theme moves the values.
  const shown = resolvedDefault(key, properties);
  let claimed = false;

  const surfaces = spec.surfaces.map(entry => {
    const value = props[entry.from] || resolvedDefault(entry.from, properties);
    const isShown = !claimed && value === shown;
    if (isShown) claimed = true;
    return { label: entry.label, value: value, shown: isShown };
  });

  return { note: spec.note, surfaces: surfaces };
}

/**
 * Sizes that cost something at the narrow end, disclosed only once reached.
 *
 * MEASURED, not assumed. Tab strip scrollWidth vs clientWidth on the live beta,
 * three tabs (Transcript / Documents / Agenda), across the slider's whole range:
 *
 *     size    320px       360px      414px+
 *     18px    fits        fits       fits
 *     20px    +22 scroll  fits       fits
 *     22px    +44 scroll  +4 scroll  fits
 *     24px    +68 scroll  +28 scroll fits
 *
 * WHY CONDITIONAL RATHER THAN A DESCRIPTION LINE
 * Nothing overflows at 414px or wider at any size the slider offers, so a
 * permanent caution is wrong for most embeds and trains people to ignore it.
 * And the description sits behind the info tip, which nobody opens until they
 * are already confused. Shown at the threshold, it is right every time it
 * appears.
 *
 * WHY A NOTE AND NOT A WARNING
 * The strip scrolls; every tab stays reachable. This is a tradeoff the admin is
 * entitled to make, so it reads as information -- the same grey as INERT_UNLESS,
 * not the contrast warning's amber. Consistent with "automate inertness,
 * surface taste": this is taste.
 */
export const SIZE_CAUTIONS = {
  themeTabFontSize: {
    atOrAbove: 20,
    message: 'At this size the tab strip scrolls sideways on narrow phones -- ' +
      'from 20px at 320px wide, from 22px at 360px. Nothing is hidden and every ' +
      'tab stays reachable. Anything wider than about 414px always fits; check ' +
      'the Narrow preview if most of your visitors are on a phone.',
  },
};

/**
 * The caution for a control, if its current value has reached the threshold.
 *
 * @param {string} key
 * @param {object} properties  the preference being edited (merged edits)
 * @returns {{message: string}|null}
 */
export function sizeCaution (key, properties) {
  const rule = SIZE_CAUTIONS[key];
  if (!rule || !properties) return null;

  const raw = properties[key];
  if (!raw) return null;

  // Only a plain length can be compared to a threshold. A client recipe --
  // clamp(), calc(), a keyword -- has no single number, and guessing at one to
  // fire a caution would be worse than staying quiet.
  const px = parseFloat(String(raw).trim());
  if (!Number.isFinite(px) || !/^[\d.]+px$/.test(String(raw).trim())) return null;

  return px >= rule.atOrAbove ? { message: rule.message } : null;
}

/** `color-mix(in srgb, <hex>, white 30%)`, for the two divider tokens. */
function mixWhite (hex, amount) {
  const rgb = parseHex(hex);
  if (!rgb) return null;
  return rgbToHex(rgb.map(c => c + (255 - c) * amount));
}

/**
 * What a control should DISPLAY when its stored value is ''.
 *
 * Follows the derivation graph as far as it goes, then lands on the player's
 * shipped constant. Never written to a preference -- storing it would turn an
 * unthemed pref into a themed one and opt it out of fill-only fan-out.
 *
 * Falls back to the static swatch whenever the chain cannot be computed: a
 * source token set to `oklch()` or `transparent` has no hex to mix, and a
 * wrong answer here is worse than the old fixed one.
 *
 * @param {string} key
 * @param {object} properties  the preference being edited (merged edits)
 * @param {number} [depth]     internal -- guards a malformed cycle in the table
 * @returns {string} a CSS colour or length, for display only
 */
export function resolvedDefault (key, properties, depth) {
  const guard = depth || 0;
  const edge = RESOLVED_FROM[key];
  if (!edge || guard > 5) return DEFAULT_SWATCHES[key];

  // A single `from` is a one-link chain, so both forms take the same path.
  const chain = Array.isArray(edge.from) ? edge.from : [edge.from];
  const props = properties || {};

  let source = null;
  for (let i = 0; i < chain.length; i++) {
    if (props[chain[i]]) { source = props[chain[i]]; break; }
  }
  // Nothing along the chain is set, so the terminal link's own default is what
  // paints -- recursing on it also picks up ITS edge if it has one.
  if (source === null) {
    source = resolvedDefault(chain[chain.length - 1], properties, guard + 1);
  }

  if (edge.via === 'mix') {
    const mixed = mixWhite(source, 0.3);
    return mixed || DEFAULT_SWATCHES[key];
  }
  return source || DEFAULT_SWATCHES[key];
}

/**
 * The tab-outline pairing.
 *
 * `themeTabBorderWidth` defaults to `0`, deliberately: a `1px` default would
 * give every client's tabs a transparent border and grow each one by 2px
 * whether or not they ever opened the Theme Builder. The consequence is that
 * choosing an outline COLOUR alone is structurally inert -- the player renders
 * `border: 0 solid <your colour>` (`global.css:397`).
 *
 * Three places already document that the editor closes this gap, and until now
 * none of them was true:
 *
 *   - `hosted-player/src/app/styles/global.css:393-396`
 *   - PMM `src/schema.ts:144-146`
 *   - this repo's own `playerPreferenceMeta` descriptions for
 *     `themeTabBorder` and `themeTabBorderWidth` -- which render in the admin
 *     UI, so the control was telling the user it did this.
 *
 * WHY AUTOMATE THIS ONE AND NOT THE OTHER PAIRS
 * Setting an outline colour has exactly one possible intent: you want to see
 * an outline. There is no competing reading to guess at, which is what
 * separates it from the contrast suggestions -- those are legibility
 * judgements, so they detect and offer rather than apply (invariant 8). The
 * other two pairing gaps (a strip rule under boxed tabs, an alternate row
 * background without a base) are taste, not inertness, and are deliberately
 * left alone. The policy is: automate inertness, surface taste.
 *
 * ONE WIDTH, TWO COLOURS
 * `--player-tab-border-width` is shared by the inactive and active tab rules
 * (`global.css:397` and `:423`) -- on purpose, so turning an outline on does
 * not shift the active tab. So either colour triggers the pairing, and the
 * width is only released when NEITHER outline is asking for it.
 *
 * PROVENANCE, NOT MEMORY
 * Nothing is persisted to say "the editor wrote this". The guard is that the
 * pairing only writes into an UNSET width, and only releases a width that is
 * exactly the `1px` it would have written. A deliberate `3px` is therefore
 * never clobbered and never silently dropped -- at the cost that a hand-typed
 * `1px` is treated as ours, which is the cheap end of the trade.
 *
 * @param {string} key                 the key being edited
 * @param {string} value               its incoming value
 * @param {object} properties          the preference as it stands (pre-edit)
 * @returns {object|null} additional writes to apply, or null
 */
export function pairTabOutline (key, value, properties) {
  if (key !== 'themeTabBorder' && key !== 'themeTabActiveBorder') return null;

  const props = properties || {};
  const width = props.themeTabBorderWidth;
  const visible = v => Boolean(v) && String(v).trim().toLowerCase() !== 'transparent';

  if (visible(value)) {
    // Only into an unset width -- never over a deliberate one.
    return width ? null : { themeTabBorderWidth: PAIRED_TAB_BORDER_WIDTH };
  }

  // Cleared or made transparent. The width is still earning its keep if the
  // OTHER outline colour is visible, so check that before releasing it.
  const otherKey = key === 'themeTabBorder' ? 'themeTabActiveBorder' : 'themeTabBorder';
  if (visible(props[otherKey])) return null;

  return width === PAIRED_TAB_BORDER_WIDTH ? { themeTabBorderWidth: '' } : null;
}

/** The width the pairing writes, and the only one it will take back. */
export const PAIRED_TAB_BORDER_WIDTH = '1px';

/**
 * Controls that paint nothing until something else is set.
 *
 * The same shape as the tab-outline pairing, but the other side of the R7
 * policy: automate inertness only where the intent is unambiguous, surface it
 * where it is not. Choosing an outline COLOUR can only mean you want an
 * outline, so the width is written for you. Choosing a corner radius cannot
 * tell us what fill you would have wanted, and inventing one would be a far
 * bigger inference than adding a 1px border -- so these say so instead.
 *
 * `needs` is satisfied when ANY listed token paints, because any one of them
 * gives the box an edge for the radius to round.
 */
export const INERT_UNLESS = {
  themeTabRadius: {
    needs: ['themeTabBg', 'themeTabBorderWidth'],
    message: 'No visible effect yet: an unselected tab is transparent with no outline, ' +
      'so it has no corners to round. Give the tabs a fill or an outline first.',
  },
  themeTabActiveRadius: {
    needs: ['themeTabActiveBg'],
    message: 'No visible effect yet: the selected tab has no fill, so it has no corners ' +
      'to round. Set Active Tab Fill first.',
  },
  themeListRowBgAlt: {
    needs: ['themeListRowBg'],
    message: 'Alternate rows only read as banding against a base row color. ' +
      'Set List Row Background too, or every other row will look like the odd one out.',
  },
  themeTranscriptRowBgAlt: {
    needs: ['themeTranscriptRowBg', 'themeListRowBg'],
    message: 'Alternate rows only read as banding against a base row color. ' +
      'Set a transcript row background too.',
  },
};

/**
 * Is this control currently inert, and why?
 *
 * Only reports when the control HAS a value -- an empty one is not doing
 * nothing, it is simply unset, and saying so on every blank field would be
 * noise on a screen built to reduce it.
 *
 * @param {string} key
 * @param {object} properties
 * @returns {{message: string}|null}
 */
export function inertWarning (key, properties) {
  const rule = INERT_UNLESS[key];
  if (!rule || !properties || !properties[key]) return null;
  const satisfied = rule.needs.some(need => {
    const value = properties[need];
    if (!value) return false;
    const v = String(value).trim().toLowerCase();
    // A transparent fill or a zero width gives the box no edge either.
    return v !== 'transparent' && parseFloat(v) !== 0;
  });
  return satisfied ? null : { message: rule.message };
}

export const MODAL_TEXT_FALLBACK = {
  themeTextStrong: '#262626',
  themeText: '#404040',
  themeTextMuted: '#737373',
  themeTextSoft: '#a3a3a3',
};

/** WCAG AA for normal text. Below this a fg/bg pair is treated as illegible. */
export const CONTRAST_MIN = 4.5;

/**
 * The two legible text ramps the suggestion offers when only the card is set.
 * `light` is the Dark preset's ramp (for a dark card); `dark` is the player's
 * own island floor (for a light card). Both are proven against their card and
 * read as a strong->soft hierarchy, so applying one is a complete text set,
 * not a single patched color.
 */
export const LEGIBLE_MODAL_TEXT = {
  light: {
    themeTextStrong: '#f5f5f5',
    themeText: '#e5e5e5',
    themeTextMuted: '#b5b5b5',
    themeTextSoft: '#8f8f8f',
  },
  dark: {
    themeTextStrong: '#262626',
    themeText: '#404040',
    themeTextMuted: '#737373',
    themeTextSoft: '#a3a3a3',
  },
};

/** Parse `#rgb`/`#rrggbb` to [r,g,b] 0-255, or null when it is not a hex color. */
function parseHex (value) {
  if (typeof value !== 'string') return null;
  let hex = value.trim().replace(/^#/, '');
  if (hex.length === 3) hex = hex[0] + hex[0] + hex[1] + hex[1] + hex[2] + hex[2];
  if (!/^[0-9a-fA-F]{6}$/.test(hex)) return null;
  return [
    parseInt(hex.slice(0, 2), 16),
    parseInt(hex.slice(2, 4), 16),
    parseInt(hex.slice(4, 6), 16),
  ];
}

function relLuminance (rgb) {
  const lin = rgb.map(c => {
    const s = c / 255;
    return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
  });
  return 0.2126 * lin[0] + 0.7152 * lin[1] + 0.0722 * lin[2];
}

/** [r,g,b] (any range) -> `#rrggbb`, clamped and rounded. */
function rgbToHex (rgb) {
  return '#' + rgb.map(c => {
    const v = Math.max(0, Math.min(255, Math.round(c)));
    return (v < 16 ? '0' : '') + v.toString(16);
  }).join('');
}

/** Relative luminance of a hex color (0..1), or 0 when it is not hex. */
function lum (hex) {
  const rgb = parseHex(hex);
  return rgb ? relLuminance(rgb) : 0;
}

/**
 * WCAG contrast ratio (1..21) between two hex colors, or null when either
 * is not a hex color (e.g. an unset '' or `transparent`).
 */
export function contrastRatio (a, b) {
  const ra = parseHex(a);
  const rb = parseHex(b);
  if (!ra || !rb) return null;
  const la = relLuminance(ra);
  const lb = relLuminance(rb);
  const hi = Math.max(la, lb);
  const lo = Math.min(la, lb);
  return (hi + 0.05) / (lo + 0.05);
}

/**
 * A legibility warning for the themed dialog background, or null.
 *
 * Only fires once the client has opted in -- `themeModal` set to a hex
 * color. An unset (or `transparent`) value keeps the white-card floor,
 * which the player renders with legible dark ink, so there is nothing to
 * warn about. The effective dialog text for each level is the themed token
 * when set, else `MODAL_TEXT_FALLBACK`; a dark card against dark (or unset)
 * text is the pairing this catches. Checks the strong and body levels --
 * the copy a reader actually needs.
 *
 * @param {object} properties  the merged preference bag
 * @returns {string|null} a human-readable warning, or null when legible
 */
export function modalContrastWarning (properties) {
  const bg = properties && properties.themeModal;
  if (!parseHex(bg)) return null;

  const levels = [
    { key: 'themeTextStrong', label: 'strong text' },
    { key: 'themeText', label: 'body text' },
  ];

  for (let i = 0; i < levels.length; i++) {
    const { key, label } = levels[i];
    const set = properties[key];
    const text = parseHex(set) ? set : MODAL_TEXT_FALLBACK[key];
    const ratio = contrastRatio(bg, text);
    if (ratio !== null && ratio < CONTRAST_MIN) {
      return 'The dialog background is too close to the ' + label +
        ' color to read (contrast ' + ratio.toFixed(1) + ':1, needs ' +
        CONTRAST_MIN + ':1). Pair a dark background with lighter text, ' +
        'or clear it for the default white card.';
    }
  }

  return null;
}

/**
 * The actionable twin of `modalContrastWarning`: a one-click fix for an
 * illegible themed dialog, or null when the dialog reads fine (or is unset).
 *
 * Fires on the SAME threshold as the warning -- `themeModal` is a hex color
 * and the effective strong or body text falls below `CONTRAST_MIN`
 * against it -- but instead of only describing the problem it derives a
 * concrete, self-consistent fix the Builder can apply on a click. It never
 * applies anything itself; it returns the values and lets the user decide.
 *
 * The fix keeps the client's card -- the deliberate opt-in token -- and moves
 * the text to read on it, whether or not the text was already set:
 *
 *   - Derive a legible text ramp (LEGIBLE_MODAL_TEXT) whose polarity reads on
 *     the card: light text for a dark card, dark text for a light one. This is
 *     the fix in the common case, and the one the user picked for the
 *     both-explicit conflict too -- text is the side that yields.
 *
 *   - Only when NO ramp can clear the threshold (a mid-tone card that neither
 *     light nor dark text reads on) does the fix fall back to the CARD: scale
 *     its own hue toward black or white just far enough to pass, keeping the
 *     hue and leaving the text alone. A mid-tone card cannot be saved by text,
 *     so here the card is the only lever left.
 *
 * Returned shape:
 *   { kind, ratio, label, message, values, swatches, preview }
 *   - kind      'text' | 'card' -- which lever the fix moves
 *   - ratio     the current worst contrast (what the warning reports)
 *   - label     the button text ("Use light text", "Darken the dialog", ...)
 *   - message   the callout sentence
 *   - values    a partial theme bag to hand to storeEdits, key by key
 *   - swatches  hex chips to preview the fix
 *   - preview   the resulting card color, for the swatch backdrop
 *
 * @param {object} properties  the merged preference bag
 * @returns {object|null}
 */
export function modalSuggestion (properties) {
  const bg = properties && properties.themeModal;
  if (!parseHex(bg)) return null;

  const strongSet = !!parseHex(properties.themeTextStrong);
  const bodySet = !!parseHex(properties.themeText);
  const strong = strongSet ? properties.themeTextStrong : MODAL_TEXT_FALLBACK.themeTextStrong;
  const body = bodySet ? properties.themeText : MODAL_TEXT_FALLBACK.themeText;

  const rStrong = contrastRatio(bg, strong);
  const rBody = contrastRatio(bg, body);
  if (rStrong >= CONTRAST_MIN && rBody >= CONTRAST_MIN) return null;
  const ratio = Math.min(rStrong, rBody);

  const message = 'Dialog text is hard to read on this background (contrast ' +
    ratio.toFixed(1) + ':1, needs ' + CONTRAST_MIN + ':1).';

  // Keep the card, move the text: derive the ramp whose polarity reads on the
  // card and apply it whether or not text was set. This is the both-explicit
  // tie-break too -- text is the side that yields. Fall through to the card fix
  // only if no ramp clears the threshold (a mid-tone card no text can save).
  const pick = contrastRatio(bg, LEGIBLE_MODAL_TEXT.light.themeTextStrong) >=
    contrastRatio(bg, LEGIBLE_MODAL_TEXT.dark.themeTextStrong)
    ? LEGIBLE_MODAL_TEXT.light : LEGIBLE_MODAL_TEXT.dark;
  if (contrastRatio(bg, pick.themeTextStrong) >= CONTRAST_MIN &&
      contrastRatio(bg, pick.themeText) >= CONTRAST_MIN) {
    return {
      kind: 'text',
      ratio,
      label: pick === LEGIBLE_MODAL_TEXT.light ? 'Use light text' : 'Use dark text',
      message,
      values: { ...pick },
      swatches: [pick.themeTextStrong, pick.themeText, pick.themeTextMuted, pick.themeTextSoft],
      preview: bg,
    };
  }

  // No ramp fits a mid-tone card: keep the text, scale the card's own hue
  // toward black or white until both strong and body clear the threshold.
  // Light text needs a darker card; dark text a lighter one.
  const rgb = parseHex(bg);
  const target = lum(strong) > 0.4 ? 0 : 255;
  let card = rgbToHex([target, target, target]);
  for (let step = 1; step <= 20; step++) {
    const t = step / 20;
    const cand = rgbToHex(rgb.map(c => c + (target - c) * t));
    if (contrastRatio(cand, strong) >= CONTRAST_MIN &&
        contrastRatio(cand, body) >= CONTRAST_MIN) {
      card = cand;
      break;
    }
  }

  const isWhite = card.toLowerCase() === '#ffffff';
  const label = isWhite ? 'Use a white card'
    : (target === 0 ? 'Darken the dialog' : 'Lighten the dialog');
  return {
    kind: 'card',
    ratio,
    label,
    message,
    values: { themeModal: card },
    swatches: [card],
    preview: card,
  };
}

// ----- Active-tab pill contrast -----

/**
 * The label inks a computed contrast pair may return, per polarity: the
 * softer brand-friendly ink first, the maximum-contrast one as the escalation
 * when the softer one cannot clear AA against the fill.
 *
 * The soft pair is the same ramp the Dark preset and the player's own island
 * floor use, so a computed label sits in the palette rather than beside it.
 * Pure black/white are the fallback precisely because they always clear AA:
 * for any background, whichever of the two has the higher ratio is >= 4.5:1.
 */
export const CONTRAST_INKS = {
  light: '#f5f5f5',
  lightMax: '#ffffff',
  dark: '#262626',
  darkMax: '#000000',
};

/**
 * The WCAG-contrast label color for a background -- what the editor computes
 * on the client's behalf for the paired ink tokens (`themeTabActiveText`, and
 * the same idea behind `themeButtonText`). PMM stores it; the player only
 * paints it. Null when `background` is not a hex color, since there is
 * nothing to compute against.
 *
 * Polarity is chosen by measurement, not by a luminance cutoff: whichever of
 * black/white scores higher against the fill decides light-or-dark ink, which
 * stays correct right through the mid-tone crossover where a fixed threshold
 * picks the wrong side.
 */
export function contrastColor (background) {
  if (!parseHex(background)) return null;
  const ramp = contrastRatio(background, CONTRAST_INKS.lightMax) >=
    contrastRatio(background, CONTRAST_INKS.darkMax)
    ? [CONTRAST_INKS.light, CONTRAST_INKS.lightMax]
    : [CONTRAST_INKS.dark, CONTRAST_INKS.darkMax];
  for (let i = 0; i < ramp.length; i++) {
    if (contrastRatio(background, ramp[i]) >= CONTRAST_MIN) return ramp[i];
  }
  return ramp[ramp.length - 1];
}

/**
 * The active tab's label ink when `themeTabActiveText` is blank. The player's
 * rule is `color: var(--player-tab-active-color, var(--color-player-text-strong))`
 * (hosted-player `global.css`), so an unset label falls through to the strong
 * text token, and that in turn to the player's own `#262626`. Filling a tab
 * without setting the label is therefore the illegible case this catches --
 * exactly the dark-on-dark shape the dialog had.
 */
export const TAB_TEXT_FALLBACK = '#262626';

/** Inactive tab labels paint on the muted token, defaulting to the player's. */
export const TAB_MUTED_FALLBACK = '#737373';

/**
 * A one-click fix for an illegible content-tab pill, or null when the tabs
 * read fine (or are unthemed). The tab twin of `modalSuggestion`, same
 * threshold, same returned shape -- so the Builder renders both the same way.
 *
 * Two pairings, checked in that order:
 *
 *   - The PILL. `themeTabActiveBg` is a hex fill and the effective label
 *     (`themeTabActiveText`, else `themeTextStrong`, else the player's own
 *     dark ink) cannot be read on it. The fix sets `themeTabActiveText` to
 *     the computed contrast ink -- the paired-ink token exists for exactly
 *     this, and it is tab-local, so nothing outside the tabs moves.
 *
 *   - The STRIP. `themeTabStripBg` is a hex tint the INACTIVE labels
 *     (`themeTextMuted`) cannot be read on. Reported without a fix on
 *     purpose: the only lever is the muted token, which also paints the
 *     event date, location and button labels across the whole player.
 *     Repainting all of that from a strip-local problem is a bigger
 *     inference than the Builder should make on one click, so this states
 *     the problem and leaves the choice with the person theming.
 *
 * @param {object} properties  the merged preference bag
 * @returns {object|null} `{ kind, ratio, label?, message, values?, swatches?, preview }`
 */
export function tabSuggestion (properties) {
  const fill = properties && properties.themeTabActiveBg;
  if (parseHex(fill)) {
    const set = properties.themeTabActiveText;
    const strong = properties.themeTextStrong;
    const label = parseHex(set) ? set
      : (parseHex(strong) ? strong : TAB_TEXT_FALLBACK);
    const ratio = contrastRatio(fill, label);
    if (ratio < CONTRAST_MIN) {
      const ink = contrastColor(fill);
      return {
        kind: 'tab-text',
        ratio,
        label: lum(ink) > 0.5 ? 'Use a light label' : 'Use a dark label',
        message: 'The active tab label is hard to read on this fill (contrast ' +
          ratio.toFixed(1) + ':1, needs ' + CONTRAST_MIN + ':1).',
        values: { themeTabActiveText: ink },
        swatches: [ink],
        preview: fill,
      };
    }
  }

  const strip = properties && properties.themeTabStripBg;
  if (parseHex(strip)) {
    const muted = properties.themeTextMuted;
    const inactive = parseHex(muted) ? muted : TAB_MUTED_FALLBACK;
    const ratio = contrastRatio(strip, inactive);
    if (ratio < CONTRAST_MIN) {
      return {
        kind: 'tab-strip',
        ratio,
        message: 'The inactive tab labels are hard to read on this strip ' +
          '(contrast ' + ratio.toFixed(1) + ':1, needs ' + CONTRAST_MIN +
          ':1). They paint on the muted text color, so lighten a dark strip ' +
          'or set a muted color that reads on it.',
        preview: strip,
      };
    }
  }

  return null;
}

/**
 * Paint a theme onto a player mount element.
 *
 * This is intentionally the same atom the player runs at boot
 * (hosted-player `applyThemeTokens`): SET a non-empty value, REMOVE an
 * empty one. Not skip-on-empty -- clearing a token in the builder has
 * to actively remove the inline property, or the previous value sticks
 * and the preview shows a theme the client would never get.
 *
 * `host` is the `.invintus-player` element the SDK mounts into, i.e.
 * the shadow host. Custom properties set there inherit through `:host`
 * into the shadow tree -- the one channel that crosses the boundary,
 * and the same path a client's own CSS override used to take.
 *
 * Idempotent; safe to call on every keystroke.
 */
export function applyThemeToHost (host, theme) {
  if (!host || !host.style) return;
  THEME_KEYS.forEach(key => {
    const value = theme ? theme[key] : '';
    if (typeof value === 'string' && value !== '') {
      host.style.setProperty(THEME_TOKEN_MAP[key], value);
    } else {
      host.style.removeProperty(THEME_TOKEN_MAP[key]);
    }
  });

  // Modal ink mirror: only when the dialog card is themed (themeModal set) do
  // the client palette values feed the dialog's `--player-modal-*` ink hooks,
  // so a themed dark card carries legible light text; unset removes them so the
  // player's white-card island floor paints its dark ink. Mirrors the player's
  // applyThemeTokens so the preview matches production. See MODAL_INK_MIRROR.
  const modalThemed = !!(theme && typeof theme.themeModal === 'string' && theme.themeModal !== '');
  Object.keys(MODAL_INK_MIRROR).forEach(key => {
    const cssVar = MODAL_INK_MIRROR[key];
    const value = theme ? theme[key] : '';
    if (modalThemed && typeof value === 'string' && value !== '') {
      host.style.setProperty(cssVar, value);
    } else {
      host.style.removeProperty(cssVar);
    }
  });
}

/** Remove every token this module manages from `host` (map + modal ink mirror). */
export function clearThemeFromHost (host) {
  if (!host || !host.style) return;
  THEME_KEYS.forEach(key => host.style.removeProperty(THEME_TOKEN_MAP[key]));
  Object.keys(MODAL_INK_MIRROR).forEach(key => host.style.removeProperty(MODAL_INK_MIRROR[key]));
}
